/**
 * Email/Password Authentication Form
 */
import React, { useState } from 'react';

const EmailAuthForm = ({ onSuccess, onCancel }) => {
  const [formData, setFormData] = useState({ name: '', email: '', password: '' });
  const [isRegistering, setIsRegistering] = useState(true);
  const [isAuthenticating, setIsAuthenticating] = useState(false);
  const [authError, setAuthError] = useState(null);

  const handleEmailAuth = async (e) => {
    e.preventDefault();
    setIsAuthenticating(true);
    setAuthError(null);

    const timeoutId = setTimeout(() => {
      console.warn('⚠️ Email auth timeout - resetting authenticating state');
      setIsAuthenticating(false);
      setAuthError('Authentication timed out. Please try again.');
    }, 10000);

    try {
      if (!formData.email || !formData.password) {
        throw new Error('Email and password are required');
      }
      
      if (isRegistering && !formData.name) {
        throw new Error('Name is required for registration');
      }

      if (formData.password.length < 6) {
        throw new Error('Password must be at least 6 characters');
      }

      await new Promise(resolve => setTimeout(resolve, 1500));
      
      const profile = {
        name: formData.name || formData.email.split('@')[0],
        email: formData.email,
        avatar: `https://api.dicebear.com/7.x/initials/svg?seed=${formData.name || formData.email}`,
        type: 'email',
        provider_id: 'email_' + Date.now(),
        auth_method: isRegistering ? 'register' : 'login'
      };
      
      clearTimeout(timeoutId);
      onSuccess(profile);
      setIsAuthenticating(false);
      
    } catch (error) {
      clearTimeout(timeoutId);
      console.error(`❌ Email authentication error:`, error);
      setAuthError(error.message || 'Authentication failed');
      setIsAuthenticating(false);
    }
  };

  return (
    <div className="py-4">
      <div className="mb-6 text-center">
        <div className="w-16 h-16 bg-gradient-to-r from-green-500 to-emerald-500 rounded-full flex items-center justify-center mx-auto mb-4">
          <span className="text-2xl">📧</span>
        </div>
        <h4 className="text-white text-lg font-semibold mb-2">
          {isRegistering ? 'Create Account' : 'Sign In'}
        </h4>
        <p className="text-white/70 text-sm">
          {isRegistering 
            ? 'Create your fire safety account' 
            : 'Sign in to your existing account'
          }
        </p>
      </div>

      {authError && (
        <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-3 mb-4">
          <div className="flex justify-between items-start">
            <p className="text-red-400 text-sm flex-1">❌ {authError}</p>
            <button
              onClick={() => {
                setAuthError(null);
                setIsAuthenticating(false);
              }}
              className="ml-2 text-red-400 hover:text-red-300 text-xs underline"
              title="Clear error and reset"
            >
              Reset
            </button>
          </div>
        </div>
      )}

      <form onSubmit={handleEmailAuth} className="space-y-4">
        {isRegistering && (
          <div>
            <label className="block text-white/80 text-sm font-medium mb-1">Name</label>
            <input
              type="text"
              placeholder="Your full name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-green-500"
              required={isRegistering}
            />
          </div>
        )}
        
        <div>
          <label className="block text-white/80 text-sm font-medium mb-1">Email</label>
          <input
            type="email"
            placeholder="your@email.com"
            value={formData.email}
            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
            className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-green-500"
            required
          />
        </div>
        
        <div>
          <label className="block text-white/80 text-sm font-medium mb-1">Password</label>
          <input
            type="password"
            placeholder="At least 6 characters"
            value={formData.password}
            onChange={(e) => setFormData({ ...formData, password: e.target.value })}
            className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-green-500"
            required
          />
        </div>
        
        <button
          type="submit"
          disabled={isAuthenticating}
          className="w-full bg-gradient-to-r from-green-500 to-emerald-500 text-white font-semibold py-3 px-6 rounded-lg hover:from-green-600 hover:to-emerald-600 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300 transform hover:scale-105"
        >
          {isAuthenticating ? (
            <div className="flex items-center justify-center space-x-2">
              <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
              <span>{isRegistering ? 'Creating Account...' : 'Signing In...'}</span>
            </div>
          ) : (
            <>{isRegistering ? '✨ Create Account' : '🔐 Sign In'}</>
          )}
        </button>
        
        <div className="flex justify-between items-center text-sm">
          <button
            type="button"
            onClick={() => setIsRegistering(!isRegistering)}
            className="text-blue-400 hover:text-blue-300 underline"
          >
            {isRegistering ? 'Already have an account?' : 'Need an account?'}
          </button>
          
          <button
            type="button"
            onClick={onCancel}
            className="text-gray-400 hover:text-gray-300"
          >
            ← Back
          </button>
        </div>
      </form>
    </div>
  );
};

export default EmailAuthForm;




